import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import ToggleSwitch from '@/components/ui/ToggleSwitch/ToggleSwitch';
import { ContactClassificationStatus } from '@/models/ContactClassificationStatus';
import { ClassificationStatusUpdateDtoOut } from '@/models/out/ClassificationStatusUpdateDtoOut';
import { queryClient } from '@/hooks/QueryClient';
import { usePatchAuthApi } from '@/hooks/UseApiCalls';
import { queryKeys } from '@/hooks/QueryKeys';
import styles from './StatusChangeDialog.module.css';

interface StatusChangeDialogProps {
  open: boolean;
  onClose: () => void;
  classificationId: number;
  newStatus: ContactClassificationStatus;
  onSuccess: () => void;
}

const getStatusLabel = (status: ContactClassificationStatus, t: (key: string) => string): string => {
  switch (status) {
    case ContactClassificationStatus.PendingApproval:
      return t('statusPendingApproval');
    case ContactClassificationStatus.Approved:
      return t('statusApproved');
    case ContactClassificationStatus.Rejected:
      return t('statusRejected');
    case ContactClassificationStatus.PendingCompletion:
      return t('statusPendingCompletion');
    case ContactClassificationStatus.Cancelled:
      return t('statusCancelled');
    case ContactClassificationStatus.CompletionReturned:
      return t('statusCompletionReturned');
    default:
      return status;
  }
};

const StatusChangeDialog = ({ open, onClose, classificationId, newStatus, onSuccess }: StatusChangeDialogProps) => {
  const { t } = useTranslation();
  const [note, setNote] = useState('');
  const [sendEmail, setSendEmail] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { mutate: updateStatus, isPending: isSubmitting } = usePatchAuthApi<void, ClassificationStatusUpdateDtoOut>(
    `management/classifications/${classificationId}/status`,
    {
      onSuccess: () => {
        // Invalidate management classifications (removes stale cached queries, only refetches active one)
        queryClient.invalidateQueries({ queryKey: queryKeys.classifications.management });
        // Invalidate cached supporteds queries of every fund (this page runs outside
        // the fund context, so there is no single "current" fund to scope to)
        queryClient.invalidateQueries({
          predicate: (q) => typeof q.queryKey[0] === 'string' && /^funds\/\d+\/supporteds/.test(q.queryKey[0]),
        });
        setNote('');
        // Called last: the parent closes the blade here, which unmounts this dialog.
        onSuccess();
      },
      onError: (err) => {
        console.error('Error updating classification status:', err);
        setError(t('errorUpdatingStatus'));
      },
    }
  );

  const handleSubmit = () => {
    setError(null);
    updateStatus({
      newStatus,
      note: note.trim() || undefined,
      showInPersonalArea: sendEmail,
      sendEmail,
    });
  };

  const handleClose = () => {
    setNote('');
    setSendEmail(false);
    setError(null);
    onClose();
  };

  return (
    <MainDialogLayout open={open} onOpenChange={(isOpen: boolean) => !isOpen && handleClose()} title={t('changeClassificationStatus')}>
      <div className={styles.content}>
        <p className="body-md-regular text-(--font-body-1)">
          {t('classificationStatusChangeConfirmation', {
            status: getStatusLabel(newStatus, t),
          })}
        </p>

        <div className={styles.emailOption}>
          <ToggleSwitch
            label={t('sendEmailNotification')}
            checked={sendEmail}
            onChange={setSendEmail}
          />
        </div>

        <div className={styles.noteSection}>
          <label htmlFor="note" className="body-sm-semibold text-(--font-body-1)">
            {t('note')}
          </label>
          <textarea
            id="note"
            className={styles.textarea}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('enterNoteAboutStatusChange')}
            rows={3}
          />
          {sendEmail && (
            <p className="body-xs-sm-regular text-(--font-body-2)">
              {t('noteSentByEmail')}
            </p>
          )}
        </div>

        {error && <p className={styles.error}>{error}</p>}

        <div className={styles.actions}>
          <button className={styles.secondaryButton} onClick={handleClose} disabled={isSubmitting}>
            {t('close')}
          </button>
          <button className={styles.primaryButton} onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? t('loading') : sendEmail ? t('saveAndSend') : t('save')}
          </button>
        </div>
      </div>
    </MainDialogLayout>
  );
};

export default StatusChangeDialog;
